import { PetType, UpdatePreReservationDto } from '@orcar/common';
import { cn } from '@orcar/common-d2c';
import { type ReactNode, useState } from 'react';
import {
  PET_BREED_PLACEHOLDER,
  PET_ERROR_POLICY,
  PET_ERROR_TYPE,
  PET_ERROR_WEIGHT_EMPTY,
  PET_ERROR_WEIGHT_OVER,
  PET_FORM_DESCRIPTION,
  PET_FORM_TITLE,
  PET_MAX_COUNT,
  PET_MAX_WEIGHT_KG,
  PET_POLICY_CHECKLIST,
  PET_POLICY_INFO_BUTTON,
  PET_POLICY_INFO_DESCRIPTION,
  PET_POLICY_INFO_TITLE,
  PET_POLICY_SECTION_TITLE,
  PET_POLICY_TOOLTIP,
  PET_TYPE_OPTIONS,
  PET_TYPE_PLACEHOLDER,
  PET_WEIGHT_GUIDE,
  PET_WEIGHT_PLACEHOLDER,
} from '@/constants/pet-policy';
import {
  trackPetPolicyAgreed,
  trackPetPolicyGuideViewed,
} from '@/utils/analytics';
import PetPreparationSheet from './PetPreparationSheet';

/** 반려동물 1마리의 입력 값. */
export type PetEntry = {
  petType: PetType | null;
  petBreed: string;
  petWeightKg: string;
};

/** 반려동물 동반 정보 입력 폼의 로컬 상태 값. */
export type PetFormValue = {
  /** 반려동물별 입력 (1~PET_MAX_COUNT마리) */
  pets: PetEntry[];
  /** 하단 필수 동의 체크 (PET_POLICY_CHECKLIST와 동일 길이) */
  policyChecks: boolean[];
};

const EMPTY_PET_ENTRY: PetEntry = {
  petType: null,
  petBreed: '',
  petWeightKg: '',
};

export const EMPTY_PET_FORM: PetFormValue = {
  pets: [{ ...EMPTY_PET_ENTRY }],
  policyChecks: PET_POLICY_CHECKLIST.map(() => false),
};

const isWeightValid = (weight: number) =>
  !Number.isNaN(weight) && weight > 0 && weight < PET_MAX_WEIGHT_KG;

const isEntryValid = (entry: PetEntry): boolean =>
  (entry.petType === PetType.DOG || entry.petType === PetType.CAT) &&
  isWeightValid(Number(entry.petWeightKg));

/**
 * 반려동물 동반 예약 진행을 위한 필수 입력/동의 충족 여부.
 * (각 반려동물의 종류·무게(10kg 미만), 그리고 하단 필수 체크 전부)
 */
export const isPetFormValid = (value: PetFormValue): boolean => {
  if (value.pets.length === 0) return false;
  if (!value.pets.every(isEntryValid)) return false;
  return value.policyChecks.every(Boolean);
};

const PET_TYPE_LABEL = PET_TYPE_OPTIONS.reduce<Record<string, string>>(
  (acc, option) => ({ ...acc, [option.value]: option.label }),
  {},
);

/**
 * 폼 상태를 UpdatePreReservationDto의 반려동물 필드로 변환한다.
 * 여러 마리는 품종(petBreed)에 텍스트로 합쳐 저장한다.
 * (ERP는 별도 폼 없이 이 텍스트를 그대로 노출 — "강아지 말티즈 3kg, 고양이 2kg")
 */
export const toPetUpdateInput = (
  value: PetFormValue,
): Pick<
  UpdatePreReservationDto,
  'petType' | 'petCount' | 'petTotalWeightKg' | 'petBreed' | 'petPolicyAgreed'
> => {
  const totalWeight = value.pets.reduce(
    (sum, pet) => sum + (Number(pet.petWeightKg) || 0),
    0,
  );

  // 1마리: 품종만 저장 (종류·무게는 별도 필드에 이미 있음)
  // 여러 마리: 펫별 상세를 합쳐 텍스트로 저장 ("강아지 말티즈 3kg, 고양이 2kg")
  const breedText =
    value.pets.length <= 1
      ? value.pets[0]?.petBreed.trim() || undefined
      : value.pets
          .map((pet) => {
            const label = pet.petType ? PET_TYPE_LABEL[pet.petType] : '';
            const breed = pet.petBreed.trim();
            const weight = pet.petWeightKg ? `${pet.petWeightKg}kg` : '';
            return [label, breed, weight].filter(Boolean).join(' ');
          })
          .filter(Boolean)
          .join(', ');

  return {
    petType: value.pets[0]?.petType ?? undefined,
    petCount: value.pets.length,
    petTotalWeightKg: totalWeight || undefined,
    petBreed: breedText || undefined,
    petPolicyAgreed: value.policyChecks.every(Boolean),
  };
};

/** 라벨 (필수 * 포함) */
const FieldLabel = ({
  children,
  required = true,
}: {
  children: ReactNode;
  required?: boolean;
}) => (
  <p className='sub3 text-grey-070'>
    {children}
    {required && <span className='text-system-error'> *</span>}
  </p>
);

const FieldError = ({ message }: { message?: string }) =>
  message ? <p className='caption1 text-system-error'>{message}</p> : null;

/** 숫자 입력 + 단위 (스텝퍼 없이 자유 입력) */
const UnitInput = ({
  value,
  onChange,
  unit,
  placeholder,
  inputMode = 'numeric',
  hasError,
}: {
  value: string;
  onChange: (v: string) => void;
  unit: string;
  placeholder?: string;
  inputMode?: 'numeric' | 'decimal';
  hasError?: boolean;
}) => (
  <div
    className={cn(
      'flex items-center rounded-s border px-4 py-2',
      hasError ? 'border-system-error' : 'border-grey-020',
    )}
  >
    <input
      type='text'
      inputMode={inputMode}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className='body2 w-full min-w-0 bg-transparent text-grey-090 outline-none'
    />
    <span className='body2 ml-2 shrink-0 text-grey-070'>{unit}</span>
  </div>
);

/** 필수 동의 체크박스 */
const PolicyCheckbox = ({
  checked,
  onToggle,
  label,
}: {
  checked: boolean;
  onToggle: () => void;
  label: string;
}) => (
  <button
    type='button'
    onClick={onToggle}
    className='flex items-start gap-2 text-left'
  >
    <span
      className={cn(
        'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-[4px] border',
        checked
          ? 'border-primary-050 bg-primary-050'
          : 'border-grey-030 bg-grey-000',
      )}
    >
      {checked && (
        <svg width='12' height='12' viewBox='0 0 12 12' fill='none'>
          <path
            d='M2.5 6.2L4.8 8.5L9.5 3.5'
            stroke='white'
            strokeWidth='1.6'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      )}
    </span>
    <span className='body2 text-grey-090'>
      <span className='font-bold text-primary-050'>[필수]</span> {label}
    </span>
  </button>
);

/** 반려동물 1마리 입력 (종류 / 품종 / 무게) */
const PetEntryFields = ({
  entry,
  index,
  isMobile,
  canRemove,
  onChange,
  onRemove,
}: {
  entry: PetEntry;
  index: number;
  isMobile: boolean;
  canRemove: boolean;
  onChange: (partial: Partial<PetEntry>) => void;
  onRemove: () => void;
}) => {
  const typeError = entry.petType ? undefined : PET_ERROR_TYPE;
  const weightError = !entry.petWeightKg
    ? PET_ERROR_WEIGHT_EMPTY
    : !isWeightValid(Number(entry.petWeightKg))
      ? PET_ERROR_WEIGHT_OVER
      : undefined;

  return (
    <div className='flex flex-col gap-4'>
      {canRemove && (
        <div className='flex items-center justify-between'>
          <p className='sub3 font-bold text-grey-090'>반려동물 {index + 1}</p>
          <button
            type='button'
            onClick={onRemove}
            className='caption1 text-grey-050 underline'
          >
            삭제
          </button>
        </div>
      )}

      {/* 종류 / 품종 */}
      <div className={cn('grid gap-4', isMobile ? 'grid-cols-1' : 'grid-cols-2')}>
        <div className='flex flex-col gap-2'>
          <FieldLabel>반려동물 종류</FieldLabel>
          <select
            value={entry.petType ?? ''}
            onChange={(e) =>
              onChange({ petType: (e.target.value || null) as PetType | null })
            }
            className={cn(
              'body2 rounded-s border bg-grey-000 px-4 py-2 outline-none',
              entry.petType ? 'text-grey-090' : 'text-grey-040',
              typeError ? 'border-system-error' : 'border-grey-020',
            )}
          >
            <option value='' disabled hidden>
              {PET_TYPE_PLACEHOLDER}
            </option>
            {PET_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <FieldError message={typeError} />
        </div>

        <div className='flex flex-col gap-2'>
          <FieldLabel required={false}>품종 / 종</FieldLabel>
          <input
            type='text'
            value={entry.petBreed}
            onChange={(e) => onChange({ petBreed: e.target.value })}
            maxLength={50}
            placeholder={PET_BREED_PLACEHOLDER}
            className='body2 rounded-s border border-grey-020 px-4 py-2 text-grey-090 outline-none'
          />
        </div>
      </div>

      {/* 무게 */}
      <div className={cn('grid gap-4', isMobile ? 'grid-cols-1' : 'grid-cols-2')}>
        <div className='flex flex-col gap-2'>
          <FieldLabel>무게 (kg)</FieldLabel>
          <UnitInput
            value={entry.petWeightKg}
            onChange={(v) => onChange({ petWeightKg: v.replace(/[^0-9.]/g, '') })}
            unit='kg'
            inputMode='decimal'
            placeholder={PET_WEIGHT_PLACEHOLDER}
            hasError={!!weightError}
          />
          <p className='caption1 text-grey-060'>{PET_WEIGHT_GUIDE}</p>
          <FieldError message={weightError} />
        </div>
      </div>
    </div>
  );
};

type PetInfoFormProps = {
  value: PetFormValue;
  onChange: (value: PetFormValue) => void;
  isMobile?: boolean;
};

const PetInfoForm = ({ value, onChange, isMobile = false }: PetInfoFormProps) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const update = (partial: Partial<PetFormValue>) =>
    onChange({ ...value, ...partial });

  const updatePet = (index: number, partial: Partial<PetEntry>) =>
    update({
      pets: value.pets.map((pet, i) =>
        i === index ? { ...pet, ...partial } : pet,
      ),
    });

  const addPet = () => {
    if (value.pets.length >= PET_MAX_COUNT) return;
    update({ pets: [...value.pets, { ...EMPTY_PET_ENTRY }] });
  };

  const removePet = (index: number) => {
    if (value.pets.length <= 1) return;
    update({ pets: value.pets.filter((_, i) => i !== index) });
  };

  const policyError = value.policyChecks.every(Boolean)
    ? undefined
    : PET_ERROR_POLICY;

  const toggleCheck = (index: number) => {
    const next = value.policyChecks.map((c, i) => (i === index ? !c : c));
    update({ policyChecks: next });
    if (!value.policyChecks.every(Boolean) && next.every(Boolean)) {
      trackPetPolicyAgreed('manual');
    }
  };

  const checkAll = () => {
    update({ policyChecks: value.policyChecks.map(() => true) });
    setSheetOpen(false);
    trackPetPolicyAgreed('guide_confirm');
  };

  return (
    <div className='flex flex-col gap-6 py-[30px]'>
      <div className='flex flex-col gap-1'>
        <p className='sub1 font-bold text-grey-090'>{PET_FORM_TITLE}</p>
        <p className='body2 text-grey-060'>{PET_FORM_DESCRIPTION}</p>
      </div>

      {/* 반려동물별 입력 */}
      <div className='flex flex-col gap-5'>
        {value.pets.map((pet, index) => (
          <div key={index} className='flex flex-col gap-5'>
            {index > 0 && <hr className='border-grey-010' />}
            <PetEntryFields
              entry={pet}
              index={index}
              isMobile={isMobile}
              canRemove={value.pets.length > 1}
              onChange={(partial) => updatePet(index, partial)}
              onRemove={() => removePet(index)}
            />
          </div>
        ))}

        {/* 추가하기 (최대 PET_MAX_COUNT마리) — 보더+색상 버튼 */}
        {value.pets.length < PET_MAX_COUNT && (
          <button
            type='button'
            onClick={addPet}
            className='sub3 self-start rounded-s border border-primary-050 px-5 py-2.5 font-bold text-primary-050'
          >
            + 반려동물 추가하기
          </button>
        )}
      </div>

      <hr className='border-grey-010' />

      {/* 이용 규정 동의 */}
      <div className='flex flex-col gap-3'>
        <div className='flex items-center gap-1.5'>
          <p className='sub2 font-bold text-grey-090'>
            {PET_POLICY_SECTION_TITLE}
          </p>
          <span className='relative flex'>
            <button
              type='button'
              aria-label='이용 규정 동의 안내'
              onClick={() => setTipOpen((v) => !v)}
              onMouseEnter={() => setTipOpen(true)}
              onMouseLeave={() => setTipOpen(false)}
              className='flex size-4 items-center justify-center rounded-full border border-primary-050 text-[10px] font-bold text-primary-050'
            >
              i
            </button>
            {tipOpen && (
              <span className='caption1 absolute left-0 top-full z-20 mt-1.5 w-[260px] max-w-[78vw] rounded-s bg-grey-090 px-3 py-2 leading-relaxed text-grey-000 shadow-md'>
                {PET_POLICY_TOOLTIP}
              </span>
            )}
          </span>
        </div>

        {/* 인포 박스 */}
        <div className='flex flex-col gap-3 rounded-s border border-dashed border-primary-050/40 bg-primary-050/[0.04] p-4'>
          <div className='flex flex-col gap-1'>
            <p className='sub3 font-bold text-primary-050'>
              {PET_POLICY_INFO_TITLE}
            </p>
            <p className='caption1 text-grey-070'>
              {PET_POLICY_INFO_DESCRIPTION}
            </p>
          </div>
          <button
            type='button'
            onClick={() => {
              setSheetOpen(true);
              trackPetPolicyGuideViewed();
            }}
            className='sub3 rounded-s border border-grey-020 bg-grey-000 py-3 font-bold text-primary-050'
          >
            {PET_POLICY_INFO_BUTTON}
          </button>
        </div>

        {/* 필수 체크 4개 */}
        <div className='flex flex-col gap-3 pt-1'>
          {PET_POLICY_CHECKLIST.map((label, index) => (
            <PolicyCheckbox
              key={label}
              checked={value.policyChecks[index]}
              onToggle={() => toggleCheck(index)}
              label={label}
            />
          ))}
          <FieldError message={policyError} />
        </div>
      </div>

      <PetPreparationSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onConfirm={checkAll}
        isMobile={isMobile}
      />
    </div>
  );
};

export { PetInfoForm };
